import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange, validateRangeAllowZero } from '../../range-validate';
import { HraCalculatorService, HraResult, HRA_CONSTRAINTS, HRA_METRO_CITIES, HRA_NON_METRO_CITIES } from './hra-calculator.service';

@Component({
  selector: 'app-hra-calculator',
  standalone: true,
  imports: [FormsModule, RouterLink],
  providers: [InvestmentChartService],
  templateUrl: './hra-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hra-calculator.scss']
})
export class HraCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(HraCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;

  readonly C = HRA_CONSTRAINTS;
  readonly Math = Math;
  readonly metroCities    = HRA_METRO_CITIES;
  readonly nonMetroCities = HRA_NON_METRO_CITIES;

  monthlyBasicSalary = 50_000;
  monthlyHraReceived = 20_000;
  monthlyRentPaid = 18_000;
  city = 'Delhi';
  isMetro = true;

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     HraResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.monthlyBasicSalary, this.C.basicSalary.min, this.C.basicSalary.max, 'Basic salary');
    if (e1) errors['monthlyBasicSalary'] = e1;
    const e2 = validateRangeAllowZero(this.monthlyHraReceived, this.C.hraReceived.min, this.C.hraReceived.max, 'HRA received');
    if (e2) errors['monthlyHraReceived'] = e2;
    const e3 = validateRangeAllowZero(this.monthlyRentPaid, this.C.rentPaid.min, this.C.rentPaid.max, 'Rent paid');
    if (e3) errors['monthlyRentPaid'] = e3;
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  setCity(city: string): void {
    this.city = city;
    this.isMetro = (this.metroCities as readonly string[]).includes(city);
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { monthlyBasicSalary: true, monthlyHraReceived: true, monthlyRentPaid: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate(
      this.monthlyBasicSalary, this.monthlyHraReceived, this.monthlyRentPaid, this.isMetro
    );
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdown(this.breakdownRef.nativeElement, [
        { label: 'Exempt HRA', value: r.exemptHra, color: '#10b981' },
        { label: 'Taxable HRA', value: r.taxableHra, color: '#ef4444' }
      ], 'HRA Received');
    }, 60);
  }

  reset(): void {
    this.monthlyBasicSalary = 50_000;
    this.monthlyHraReceived = 20_000;
    this.monthlyRentPaid    = 18_000;
    this.city                = 'Delhi';
    this.isMetro            = true;
    this.formErrors         = {};
    this.touched            = {};
    this.showFormErrors     = false;
    this.calculated         = false;
    this.result             = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
